import React, { useState, useEffect, useMemo } from "react";
import { DerivativeCreation } from "@/data/models/types";
import { UnifiedDerivativeItem } from "./UnifiedDerivativeItem";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useIsMobile } from "@/hooks/use-mobile";

interface ColumnBreakpoints {
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  "2xl"?: number;
}

type DisplayMode = "grid" | "list" | "carousel";

interface UnifiedDerivativeDisplayProps {
  items: DerivativeCreation[];
  displayMode?: DisplayMode;
  isLoading?: boolean;
  error?: string | null;
  columns?: ColumnBreakpoints;
  initialItemsToShow?: number;
  incrementAmount?: number;
  emptyMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  showLoadMore?: boolean;
  className?: string;
  onLoadMore?: () => void;
  forceVerticalLayout?: boolean;
  allItemsLink?: string;
  allItemsLinkText?: string;
}

export const UnifiedDerivativeDisplay = ({
  items,
  displayMode = "grid",
  isLoading = false,
  error = null,
  columns = { sm: 1, md: 2, lg: 3, xl: 3 },
  initialItemsToShow = 12,
  incrementAmount = 12,
  emptyMessage = "No items to display",
  loadingMessage = "Loading...",
  errorMessage = "Failed to load items",
  showLoadMore = true,
  className = "",
  onLoadMore,
  forceVerticalLayout = true,
  allItemsLink,
  allItemsLinkText = "View all",
}: UnifiedDerivativeDisplayProps) => {
  const { t } = useLanguage();
  const [visibleItems, setVisibleItems] = useState<number>(initialItemsToShow);
  const [isClient, setIsClient] = useState<boolean>(false);
  const isMobile = useIsMobile();
  
  // Reset visible items when items array changes
  useEffect(() => {
    setVisibleItems(initialItemsToShow);
  }, [items, initialItemsToShow]);
  
  // Set isClient to true after component mounts
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  // Increase visible items
  const loadMore = () => {
    console.log('UnifiedDerivativeDisplay - loadMore called, visible items:', visibleItems, 'increment:', incrementAmount);
    
    if (onLoadMore) {
      // If parent provided a loadMore handler, call it
      console.log('Using parent onLoadMore handler');
      onLoadMore();
    } else {
      // Otherwise handle pagination internally
      console.log('Using internal pagination');
      setVisibleItems(prev => {
        const newValue = Math.min(prev + incrementAmount, items.length);
        console.log(`Increasing visible items from ${prev} to ${newValue}`);
        
        // Show toast when all items are loaded
        if (newValue >= items.length && prev !== items.length) {
          // This toast will only be shown when we've reached the end
          // NOT on every click when already at the end
          console.log('All items loaded - could show toast here');
        }
        
        return newValue;
      });
    }
  };
  
  // Calculate if there are more items to load
  const hasMoreItems = items.length > visibleItems;
  
  if (isLoading && items.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
        <span>{loadingMessage}</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {errorMessage || error}
      </div>
    );
  }
  
  if (items.length === 0 && !isLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  // Render carousel layout
  if (displayMode === "carousel") {
    return (
      <div className={cn("w-full", className)}>
        <Carousel opts={{
            align: "start",
            loop: false,
            dragFree: false
          }}>
          <CarouselContent className="-ml-4">
            {items.slice(0, initialItemsToShow).map((item, index) => (
              <CarouselItem 
                key={`${item.id}-${index}`}
                className={`pl-4 ${isMobile ? 'basis-full sm:basis-1/2' : 'basis-1/2 md:basis-1/3 lg:basis-1/4'}`}
              >
                <UnifiedDerivativeItem 
                  derivative={item}
                  isVisible={true}
                  isInViewport={true}
                  forceVertical={forceVerticalLayout}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          
          <div className="flex justify-end mt-4">
            <div className="flex items-center gap-4">
              <CarouselPrevious className="relative static translate-y-0 translate-x-0 left-0 right-0" />
              <CarouselNext className="relative static translate-y-0 translate-x-0 left-0 right-0" />
              
              {allItemsLink && (
                <Button variant="outline" asChild>
                  <Link to={allItemsLink} className="flex items-center">
                    {allItemsLinkText} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </Carousel>
      </div>
    );
  }
  
  // Render grid or list layouts
  return (
    <div className={cn("w-full", className)}>
      {displayMode === "grid" ? (
        // Simplified grid layout with CSS grid for consistent sizing
        <div className={`grid grid-cols-1 sm:grid-cols-${columns.sm || 1} md:grid-cols-${columns.md || 2} lg:grid-cols-${columns.lg || 3} xl:grid-cols-${columns.xl || 3} gap-4`}>
          {items.slice(0, onLoadMore ? items.length : visibleItems).map((item, idx) => (
            <div key={`${item.id}-${idx}`} className="w-full h-full">
              <UnifiedDerivativeItem 
                derivative={item}
                isVisible={true}
                isInViewport={true}
                forceVertical={forceVerticalLayout}
              />
            </div>
          ))}
        </div>
      ) : (
        // List view (one column)
        <div className="flex flex-col gap-4">
          {items.slice(0, onLoadMore ? items.length : visibleItems).map((item, idx) => (
            <React.Fragment key={`${item.id}-${idx}`}>
              <UnifiedDerivativeItem 
                derivative={item} 
                isVisible={true} 
                isInViewport={true}
                forceVertical={forceVerticalLayout} 
              />
              {idx < (onLoadMore ? items.length : visibleItems) - 1 && <Separator className="my-2" />}
            </React.Fragment>
          ))}
        </div>
      )}
      
      {/* Show the load more button only if we're handling pagination internally AND there are more items to load */}
      {showLoadMore && !onLoadMore && hasMoreItems && !isLoading && (
        <div className="flex justify-center mt-8">
          <Button 
            onClick={loadMore}
            variant="default"
            size="lg"
            className="min-w-[200px]"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('common.loadMore')
            )}
          </Button>
        </div>
      )}
      
      {/* Show message when all items are loaded */}
      {showLoadMore && !onLoadMore && !hasMoreItems && items.length > 0 && (
        <div className="mt-8 text-center text-muted-foreground">
          {t('derivatives.allItemsLoaded')}
        </div>
      )}
    </div>
  );
};
